import React, { createContext, useContext, useState, useEffect } from 'react';
import { getLatestExchangeRate } from '../api/client';

const ExchangeRateContext = createContext();

export const useExchangeRate = () => {
  const context = useContext(ExchangeRateContext);
  if (!context) {
    throw new Error('useExchangeRate must be used within an ExchangeRateProvider');
  }
  return context;
};

export const ExchangeRateProvider = ({ children }) => {
  const [exchangeRate, setExchangeRate] = useState(36.50); // Default rate
  const [loading, setLoading] = useState(true);
  const [primaryCurrency, setPrimaryCurrency] = useState('USD'); // USD or VES
  const [showBothCurrencies, setShowBothCurrencies] = useState(true);

  const fetchLatestRate = async () => {
    try {
      setLoading(true);
      const latestRate = await getLatestExchangeRate();
      setExchangeRate(latestRate.usd_to_ves_rate || 36.50);
    } catch (error) {
      console.error('Error fetching exchange rate:', error);
      // Keep default rate on error
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLatestRate();
  }, []);

  const convertUSDToVES = (usdAmount) => {
    return usdAmount * exchangeRate;
  };

  const convertVESToUSD = (vesAmount) => {
    return vesAmount / exchangeRate;
  };

  const formatDualCurrency = (usdAmount, options = {}) => {
    const {
      showBoth = showBothCurrencies,
      primary = primaryCurrency,
      compact = false
    } = options;

    const vesAmount = convertUSDToVES(usdAmount);
    
    const formatUSD = (amount) => new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

    const formatVES = (amount) => new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency: 'VES',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);

    if (!showBoth) {
      return primary === 'USD' ? formatUSD(usdAmount) : formatVES(vesAmount);
    }

    if (compact) {
      return primary === 'USD' 
        ? `${formatUSD(usdAmount)} / Bs. ${vesAmount.toFixed(0)}`
        : `Bs. ${vesAmount.toFixed(2)} / ${formatUSD(usdAmount)}`;
    }

    return primary === 'USD'
      ? `${formatUSD(usdAmount)} / ${formatVES(vesAmount)}`
      : `${formatVES(vesAmount)} / ${formatUSD(usdAmount)}`;
  };

  const value = {
    exchangeRate,
    primaryCurrency,
    showBothCurrencies,
    loading,
    setPrimaryCurrency,
    setShowBothCurrencies,
    convertUSDToVES,
    convertVESToUSD,
    formatDualCurrency,
    refreshRate: fetchLatestRate
  };

  return (
    <ExchangeRateContext.Provider value={value}>
      {children}
    </ExchangeRateContext.Provider>
  );
};

export default ExchangeRateContext;